import { useState, useRef, useEffect, useContext } from 'react'
import Modal from 'components/modal/Modal';
import AuthContext from 'contexte/AuthContext';
import { fetchCioSegments } from 'services/segmentServices';
import DateSelector from 'components/Filters/DateSelector/DateSelector';
import { CAMPAIGN_PAGES, CAMPAIGN_TYPES, FEES_CATEGORIES, campaignStatus } from '../../campaignTypes';

// types dont le gain n'est pas plafonné par utilisateur (gain fixe, ou réduction overnight non plafonnée)
// Le bonus est plafonné par son propre montant.
const NO_MAXIMUM_GAINS = ['margin_trades_completed', 'overnight_fees_discount', 'bonus'];

const MAXIMUM_GAINS_HELPER = {
  structured_product: 'Cashback maximum versé à chaque utilisateur.',
  margin_gains_doubled: 'Gains supplémentaires maximum versés à chaque utilisateur.',
  reduced_fees_category: 'Réduction de frais maximum accordée à chaque utilisateur.',
  refund_margin_loss: 'Pertes remboursées au maximum à chaque utilisateur.',
}

const today = () => ({ date1: { startDate: new Date() } })

const buildEmptyForm = () => ({
  name: '',
  description: '',
  type: '',
  segment: '',
  pages: [],
  maximumGains: '',
  unlimitedGains: false,
  hasEndDate: true,
  spMinimumInvest: '',
  spCashbackPercentage: '',
  marginMinimumTrades: '',
  marginGains: '',
  reducedFeesCategory: '',
  reducedFeesPercentage: '',
  overnightPercentage: '',
  bonusAmount: '',
})

const toText = (value) => value === undefined || value === null ? '' : String(value)

// Formulaire prérempli avec une campagne existante, pour la modifier
const buildFormFromCampaign = ({ name, description, type, segment_id, pages, maximum_gains, end_date, details = {} }) => ({
  ...buildEmptyForm(),
  name: name || '',
  description: description || '',
  type,
  segment: toText(segment_id),
  pages: pages || [],
  maximumGains: maximum_gains > 0 ? String(maximum_gains) : '',
  unlimitedGains: !(maximum_gains > 0),
  hasEndDate: !!end_date,
  ...(type === 'structured_product' && { spMinimumInvest: toText(details.minimum_investment), spCashbackPercentage: toText(details.percentage) }),
  ...(type === 'margin_trades_completed' && { marginMinimumTrades: toText(details.minimum_trades), marginGains: toText(details.gains) }),
  ...(type === 'reduced_fees_category' && { reducedFeesCategory: details.category || '', reducedFeesPercentage: toText(details.percentage) }),
  ...(type === 'overnight_fees_discount' && { overnightPercentage: toText(details.percentage) }),
  ...(type === 'bonus' && { bonusAmount: toText(details.amount) }),
})

// '' reste vide au lieu de devenir 0, pour qu'un champ non rempli soit signalé
const toNumber = (value) => value === '' || value === null || value === undefined ? NaN : Number(value)

const startOfLocalDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

// L'API stocke les dates à 0 h (début) ou 23 h 59 (fin) UTC : on affiche ce même jour en heure locale
const fromUtcDay = (value) => {
  const date = new Date(value)
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

// L'API ramène la date à minuit UTC : on envoie midi UTC du jour choisi, sinon un jour choisi
// entre 0 h et 2 h (heure de Paris) partirait la veille.
const toUtcNoon = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12))

const isPercentage = (value) => toNumber(value) > 0 && toNumber(value) <= 100

// isEdit : modification d'une campagne existante, dont la date de fin ne peut pas être passée.
// offerLocked : campagne en cours, son offre et sa date de début ne changent plus (l'API les ignore).
// Une date de début passée deviendrait « maintenant » côté API : on la refuse pour que le formulaire dise vrai.
const validate = (form, startDate, endDate, { isEdit, offerLocked }) => {
  const errors = {}
  const today = startOfLocalDay(new Date())
  if (!form.name.trim()) errors.name = 'Donnez un nom à la campagne.'
  if (!form.type) errors.type = 'Choisissez un type de campagne.'
  if (!form.segment) errors.segment = 'Choisissez un segment.'
  if (!form.pages.length) errors.pages = 'Cochez au moins une page.'
  if (!startDate) errors.startDate = 'Choisissez une date de début.'
  else if (!offerLocked && startOfLocalDay(startDate) < today)
    errors.startDate = 'La date de début ne peut pas être passée.'
  if (form.type === 'bonus' && !form.hasEndDate)
    errors.endDate = "Un bonus expire à sa date de fin : indiquez-en une."
  else if (form.hasEndDate && (!endDate || (startDate && startOfLocalDay(endDate) < startOfLocalDay(startDate))))
    errors.endDate = 'La date de fin doit être le jour de début ou après.'
  else if (isEdit && form.hasEndDate && startOfLocalDay(endDate) < today)
    errors.endDate = 'La date de fin ne peut pas être passée. Pour arrêter la campagne maintenant, utilisez « Terminer » dans la liste.'

  if (offerLocked) return errors
  if (form.type && !NO_MAXIMUM_GAINS.includes(form.type) && !form.unlimitedGains && !(toNumber(form.maximumGains) > 0))
    errors.maximumGains = 'Indiquez un montant supérieur à 0 €, ou cochez « Sans plafond ».'
  switch (form.type) {
    case 'structured_product':
      if (!(toNumber(form.spMinimumInvest) >= 0)) errors.spMinimumInvest = 'Indiquez un montant de 0 € ou plus.'
      if (!isPercentage(form.spCashbackPercentage)) errors.spCashbackPercentage = 'Indiquez un pourcentage supérieur à 0 et au plus 100 %.'
      break;
    case 'margin_trades_completed':
      if (!(Number.isInteger(toNumber(form.marginMinimumTrades)) && toNumber(form.marginMinimumTrades) >= 1)) errors.marginMinimumTrades = 'Indiquez un nombre entier de trades, au moins 1.'
      if (!(toNumber(form.marginGains) > 0)) errors.marginGains = 'Indiquez un montant supérieur à 0 €.'
      break;
    case 'reduced_fees_category':
      if (!form.reducedFeesCategory) errors.reducedFeesCategory = 'Choisissez une catégorie de produit.'
      if (!isPercentage(form.reducedFeesPercentage)) errors.reducedFeesPercentage = 'Indiquez un pourcentage supérieur à 0 et au plus 100 %.'
      break;
    case 'overnight_fees_discount':
      if (!isPercentage(form.overnightPercentage)) errors.overnightPercentage = 'Indiquez un pourcentage supérieur à 0 et au plus 100 %.'
      break;
    case 'bonus':
      if (!(toNumber(form.bonusAmount) > 0) || Math.round(toNumber(form.bonusAmount) * 100) !== toNumber(form.bonusAmount) * 100)
        errors.bonusAmount = 'Indiquez un montant supérieur à 0 €, avec 2 décimales au plus.'
      break;
    default:
      break;
  }
  return errors
}


// Champ avec libellé, aide toujours visible et erreur juste en dessous, reliées pour les lecteurs d'écran
const Field = ({ id, label, helper, error, children }) => (
  <div className='campaigns_field'>
    <label className='campaigns_field_label' htmlFor={id}>{label}</label>
    {children}
    {helper && <p id={`${id}-helper`} className='campaigns_field_helper'>{helper}</p>}
    {error && <p id={`${id}-error`} className='campaigns_field_error'>{error}</p>}
  </div>
)

const describedBy = (id, helper, error) => [helper && `${id}-helper`, error && `${id}-error`].filter(Boolean).join(' ') || undefined

const NumberField = ({ id, label, helper, error, unit, value, onChange, onBlur, min = 0, max, step = 'any', disabled }) => (
  <Field id={id} label={label} helper={helper} error={error}>
    <div className={`campaigns_field_control ${error ? 'campaigns_field_invalid' : ''}`}>
      <input
        id={id}
        type='number'
        inputMode={step === 1 ? 'numeric' : 'decimal'}
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, helper, error)}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur} />
      {unit && <span className='campaigns_field_unit' aria-hidden='true'>{unit}</span>}
    </div>
  </Field>
)


// campaign : campagne à modifier, absente pour en créer une
const CampaignFormModal = ({ isModalOpen, onClose, onSubmit, campaign }) => {

  const { user, verifyUser } = useContext(AuthContext)
  const role = verifyUser()?.role
  const formRef = useRef(null)
  const filledFrom = useRef(null) // campagne chargée dans le formulaire, null = brouillon de création
  const isEdit = !!campaign
  const offerLocked = isEdit && campaignStatus(campaign) === 'running'
  const todayStart = startOfLocalDay(new Date())
  const [form, setForm] = useState(buildEmptyForm())
  const [selectedStartDate, setSelectedStartDate] = useState(today());
  const [selectedEndDate, setSelectedEndDate] = useState(today());
  const [touched, setTouched] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formKey, setFormKey] = useState(0) // les sélecteurs de date gardent leur propre état : on les remonte à la remise à zéro
  const [segments, setSegments] = useState([])
  const [segmentsStatus, setSegmentsStatus] = useState('loading') // 'loading' | 'loaded' | 'error'

  // segments Customer.io, rechargés à chaque ouverture pour voir ceux créés entre-temps
  useEffect(() => {
    if (!isModalOpen) return
    setSegmentsStatus('loading')
    fetchCioSegments(user)
      .then((data) => {
        setSegments(data)
        setSegmentsStatus('loaded')
      })
      .catch(() => setSegmentsStatus('error'))
  }, [isModalOpen])

  // à l'ouverture : préremplit avec la campagne à modifier, ou repart d'un formulaire vide après une modification
  // (le brouillon d'une création non envoyée est gardé)
  useEffect(() => {
    if (!isModalOpen) return
    if (campaign) fill(campaign)
    else if (filledFrom.current) reset()
    filledFrom.current = campaign || null
  }, [isModalOpen, campaign])

  // un bonus est donné à l'enregistrement de la campagne : elle commence forcément aujourd'hui
  const bonusStartsToday = form.type === 'bonus' && !offerLocked
  const startDate = bonusStartsToday ? todayStart : selectedStartDate.date1.startDate
  const endDate = selectedEndDate.date1.startDate
  const errors = validate(form, startDate, endDate, { isEdit, offerLocked })
  const hasMaximumGains = form.type && !NO_MAXIMUM_GAINS.includes(form.type)

  // une erreur s'affiche quand on quitte le champ, ou pour tous les champs après un premier essai de création
  const errorOf = (field) => (submitted || touched[field]) ? errors[field] : undefined
  const touch = (field) => () => setTouched(prev => ({ ...prev, [field]: true }))
  const set = (field) => (value) => setForm(prev => ({ ...prev, [field]: value }))

  const togglePage = (page) => {
    setTouched(prev => ({ ...prev, pages: true }))
    setForm(prev => ({ ...prev, pages: prev.pages.includes(page) ? prev.pages.filter((p) => p !== page) : [...prev.pages, page] }))
  }

  // un bonus a toujours une date de fin : c'est sa date d'expiration
  const setType = (type) => setForm(prev => ({ ...prev, type, hasEndDate: type === 'bonus' || prev.hasEndDate }))

  const reset = () => {
    setForm(buildEmptyForm())
    setSelectedStartDate(today())
    setSelectedEndDate(today())
    setTouched({})
    setSubmitted(false)
    setFormKey(key => key + 1)
  }

  const fill = (campaign) => {
    const start = fromUtcDay(campaign.start_date)
    setForm(buildFormFromCampaign(campaign))
    setSelectedStartDate({ date1: { startDate: start } })
    // sans date de fin, on propose le premier jour possible si on en ajoute une
    setSelectedEndDate({ date1: { startDate: campaign.end_date ? fromUtcDay(campaign.end_date) : (start > todayStart ? start : todayStart) } })
    setTouched({})
    setSubmitted(false)
    setFormKey(key => key + 1)
  }

  const buildCampaign = () => {
    const campaign = {
      name: form.name.trim(),
      type: form.type,
      segment_id: form.segment,
      pages: form.pages,
      startDate: toUtcNoon(startDate),
      details: {}
    }
    if (form.description.trim())
      campaign.description = form.description.trim()
    if (hasMaximumGains && !form.unlimitedGains)
      campaign.maximum_gains = Number(form.maximumGains);
    if (form.hasEndDate)
      campaign.endDate = toUtcNoon(endDate)

    switch (form.type) {
      case 'structured_product':
        campaign.details = { minimum_investment: Number(form.spMinimumInvest), percentage: Number(form.spCashbackPercentage) };
        break;
      case 'margin_trades_completed':
        campaign.details = { minimum_trades: Number(form.marginMinimumTrades), gains: Number(form.marginGains) };
        break;
      case 'reduced_fees_category':
        campaign.details = { category: form.reducedFeesCategory, percentage: Number(form.reducedFeesPercentage) };
        break;
      case 'overnight_fees_discount':
        campaign.details = { percentage: Number(form.overnightPercentage) };
        break;
      case 'bonus':
        campaign.details = { amount: Number(form.bonusAmount) };
        break;
      default:
        break;
    }
    return campaign
  }

  const submit = async () => {
    setSubmitted(true)
    if (Object.keys(errors).length) {
      // amène le premier champ en erreur sous les yeux de l'utilisateur
      setTimeout(() => formRef.current?.querySelector('[aria-invalid="true"]')?.focus())
      return
    }
    setSubmitting(true)
    const saved = await onSubmit(buildCampaign())
    setSubmitting(false)
    // en cas d'erreur, le formulaire reste ouvert et rempli pour pouvoir corriger
    if (saved) {
      reset()
      onClose()
    }
  }

  const close = () => { if (!submitting) onClose() }


  return (
    <Modal isOpen={isModalOpen} onClose={close} closeButton width='680px'>
      <div className='campaigns_form' ref={formRef} key={formKey}>
        <h2 className='campaigns_form_title'>{isEdit ? 'Modifier la campagne' : 'Nouvelle campagne'}</h2>
        <p className='campaigns_hint'>La campagne commence le jour de début à 0 h (UTC), ou dès son enregistrement si elle commence aujourd'hui, et s'arrête le jour de fin à 23 h 59 (UTC). Une campagne terminée dans la journée peut donc être remplacée tout de suite. Une seule campagne par famille peut être active à la fois (levier, frais overnight, produit structuré), mais des familles différentes peuvent tourner en même temps. Les campagnes bonus peuvent toutes se superposer : elles commencent le jour même et donnent leurs bonus dès leur enregistrement.</p>
        {offerLocked && <p className='campaigns_hint'>Cette campagne est en cours : son offre (type, montants, plafond) et sa date de début ne peuvent plus changer. Pour proposer une autre offre, terminez-la puis créez-en une nouvelle.</p>}

        <section className='campaigns_form_section' aria-labelledby='campaign-offer-title'>
          <h3 id='campaign-offer-title'>Offre</h3>
          <Field id='campaign-name' label='Nom' helper='Visible uniquement dans le back office.' error={errorOf('name')}>
            <input
              id='campaign-name'
              type='text'
              value={form.name}
              aria-invalid={errorOf('name') ? true : undefined}
              aria-describedby={describedBy('campaign-name', true, errorOf('name'))}
              onChange={(e) => set('name')(e.target.value)}
              onBlur={touch('name')} />
          </Field>
          <Field id='campaign-description' label='Description' helper='Facultatif. Visible uniquement dans le back office.'>
            <textarea
              id='campaign-description'
              rows={3}
              maxLength={500}
              value={form.description}
              aria-describedby={describedBy('campaign-description', true)}
              onChange={(e) => set('description')(e.target.value)} />
          </Field>
          <Field id='campaign-type' label='Type de campagne' error={errorOf('type')}>
            <select
              id='campaign-type'
              value={form.type}
              disabled={offerLocked}
              aria-invalid={errorOf('type') ? true : undefined}
              aria-describedby={describedBy('campaign-type', false, errorOf('type'))}
              onChange={(e) => setType(e.target.value)}
              onBlur={touch('type')}>
              <option value=''>Choisir une offre</option>
              {Object.entries(CAMPAIGN_TYPES)
                // bonus : réservé au rôle 1, et une campagne existante ne peut pas en devenir une (ses bonus ne seraient jamais donnés)
                .filter(([value]) => value !== 'bonus' || form.type === 'bonus' || (!isEdit && role === 1))
                .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </Field>

          {form.type === 'structured_product' && (
            <div className='campaigns_field_row'>
              <NumberField id='campaign-sp-percentage' label='Cashback' unit='%' max={100}
                helper='Pourcentage du montant investi reversé, 100 % au plus.'
                disabled={offerLocked} value={form.spCashbackPercentage} onChange={set('spCashbackPercentage')} onBlur={touch('spCashbackPercentage')} error={errorOf('spCashbackPercentage')} />
              <NumberField id='campaign-sp-minimum' label='Investissement minimum' unit='€'
                disabled={offerLocked} value={form.spMinimumInvest} onChange={set('spMinimumInvest')} onBlur={touch('spMinimumInvest')} error={errorOf('spMinimumInvest')} />
            </div>
          )}
          {form.type === 'margin_trades_completed' && (
            <div className='campaigns_field_row'>
              <NumberField id='campaign-margin-trades' label='Nombre de trades levier' step={1} min={1}
                disabled={offerLocked} value={form.marginMinimumTrades} onChange={set('marginMinimumTrades')} onBlur={touch('marginMinimumTrades')} error={errorOf('marginMinimumTrades')} />
              <NumberField id='campaign-margin-gains' label='Gain offert' unit='€'
                disabled={offerLocked} value={form.marginGains} onChange={set('marginGains')} onBlur={touch('marginGains')} error={errorOf('marginGains')} />
            </div>
          )}
          {form.type === 'reduced_fees_category' && (
            <div className='campaigns_field_row'>
              <Field id='campaign-fees-category' label='Catégorie de produit' error={errorOf('reducedFeesCategory')}>
                <select
                  id='campaign-fees-category'
                  value={form.reducedFeesCategory}
                  disabled={offerLocked}
                  aria-invalid={errorOf('reducedFeesCategory') ? true : undefined}
                  aria-describedby={describedBy('campaign-fees-category', false, errorOf('reducedFeesCategory'))}
                  onChange={(e) => set('reducedFeesCategory')(e.target.value)}
                  onBlur={touch('reducedFeesCategory')}>
                  <option value=''>Choisir une catégorie</option>
                  {Object.entries(FEES_CATEGORIES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              <NumberField id='campaign-fees-percentage' label='Réduction des frais' unit='%' max={100}
                helper='100 % = frais offerts.'
                disabled={offerLocked} value={form.reducedFeesPercentage} onChange={set('reducedFeesPercentage')} onBlur={touch('reducedFeesPercentage')} error={errorOf('reducedFeesPercentage')} />
            </div>
          )}
          {form.type === 'overnight_fees_discount' && (
            <div className='campaigns_field_row'>
              <NumberField id='campaign-overnight-percentage' label='Réduction des frais overnight' unit='%' max={100}
                helper='Part des frais overnight de la veille reversée, 100 % = frais offerts.'
                disabled={offerLocked} value={form.overnightPercentage} onChange={set('overnightPercentage')} onBlur={touch('overnightPercentage')} error={errorOf('overnightPercentage')} />
            </div>
          )}

          {form.type === 'bonus' && (
            <div className='campaigns_field_row'>
              <NumberField id='campaign-bonus-amount' label='Bonus par utilisateur' unit='€' step={0.01}
                helper="Donné à chaque client du segment dès l'enregistrement de la campagne, comme un bonus donné depuis la fiche client. Il paie la marge et les frais d'ouverture des positions levier forex et métaux, avant le portefeuille, jusqu'à la date de fin. Terminer la campagne annule ce qui reste sur ses bonus."
                disabled={offerLocked} value={form.bonusAmount} onChange={set('bonusAmount')} onBlur={touch('bonusAmount')} error={errorOf('bonusAmount')} />
            </div>
          )}

          {hasMaximumGains && (
            <div className='campaigns_field_row campaigns_field_row_end'>
              <NumberField id='campaign-maximum-gains' label='Plafond par utilisateur' unit='€'
                helper={MAXIMUM_GAINS_HELPER[form.type]}
                disabled={offerLocked || form.unlimitedGains}
                value={form.unlimitedGains ? '' : form.maximumGains} onChange={set('maximumGains')} onBlur={touch('maximumGains')} error={errorOf('maximumGains')} />
              <label className='campaigns_checkbox'>
                <input type='checkbox' disabled={offerLocked} checked={form.unlimitedGains} onChange={(e) => set('unlimitedGains')(e.target.checked)} />
                Sans plafond
              </label>
            </div>
          )}
        </section>

        <section className='campaigns_form_section' aria-labelledby='campaign-audience-title'>
          <h3 id='campaign-audience-title'>Audience</h3>
          <Field id='campaign-segment' label='Segment' error={errorOf('segment')}
            helper={segmentsStatus === 'error' ? 'Impossible de charger les segments Customer.io. Fermez puis rouvrez le formulaire.'
              : isEdit && form.type === 'bonus' ? 'Les bonus de cette campagne sont déjà donnés à ce segment : il ne peut plus changer.'
              : form.type === 'bonus' ? "Segments Customer.io. Chaque client du segment au moment de l'enregistrement reçoit le bonus."
              : 'Segments Customer.io.'}>
            <select
              id='campaign-segment'
              value={form.segment}
              disabled={segmentsStatus !== 'loaded' || (isEdit && form.type === 'bonus')}
              aria-invalid={errorOf('segment') ? true : undefined}
              aria-describedby={describedBy('campaign-segment', true, errorOf('segment'))}
              onChange={(e) => set('segment')(e.target.value)}
              onBlur={touch('segment')}>
              <option value=''>{segmentsStatus === 'loading' ? 'Chargement des segments…' : 'Choisir un segment'}</option>
              {segments.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
            </select>
          </Field>
          <fieldset className='campaigns_fieldset' aria-describedby={errorOf('pages') ? 'campaign-pages-error' : undefined}>
            <legend className='campaigns_field_label'>Pages d'affichage</legend>
            <div className='campaigns_checkbox_group'>
              {Object.entries(CAMPAIGN_PAGES).map(([value, label]) => (
                <label key={value} className='campaigns_checkbox'>
                  <input type='checkbox' checked={form.pages.includes(value)} aria-invalid={errorOf('pages') ? true : undefined} onChange={() => togglePage(value)} />
                  {label}
                </label>
              ))}
            </div>
            {errorOf('pages') && <p id='campaign-pages-error' className='campaigns_field_error'>{errorOf('pages')}</p>}
          </fieldset>
        </section>

        <section className='campaigns_form_section' aria-labelledby='campaign-dates-title'>
          <h3 id='campaign-dates-title'>Dates</h3>
          <div className='campaigns_field_row'>
            <Field id='campaign-start' label='Début' error={errorOf('startDate')}>
              {offerLocked || bonusStartsToday
                ? <p id='campaign-start' className='campaigns_readonly_value'>{startDate?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                : <DateSelector selectedDate={selectedStartDate} setSelectedDate={(value) => { setSelectedStartDate(value); touch('startDate')() }} simpleDate={true} minDate={todayStart} />}
            </Field>
            {form.hasEndDate && (
              <Field id='campaign-end' label='Fin' error={errorOf('endDate')}
                helper={form.type === 'bonus' ? "Date d'expiration des bonus donnés. Elle ne pourra plus changer après l'enregistrement." : undefined}>
                {offerLocked && form.type === 'bonus'
                  ? <p id='campaign-end' className='campaigns_readonly_value'>{endDate?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  : <DateSelector selectedDate={selectedEndDate} setSelectedDate={(value) => { setSelectedEndDate(value); touch('endDate')() }} simpleDate={true} minDate={isEdit && todayStart > startDate ? todayStart : startDate} />}
              </Field>
            )}
          </div>
          {/* un bonus a toujours une date de fin : pas d'interrupteur */}
          {form.type !== 'bonus' && (
            <>
              <div className='campaigns_switch_row'>
                <p id='campaign-end-label'>Date de fin</p>
                <label className='toggle campaigns_toggle'>
                  <input className='toggle-checkbox'
                    type='checkbox'
                    role='switch'
                    aria-labelledby='campaign-end-label'
                    aria-describedby='campaign-end-hint'
                    checked={form.hasEndDate}
                    onChange={(e) => set('hasEndDate')(e.target.checked)}
                  />
                  <div className='toggle-switch'></div>
                </label>
              </div>
              <p id='campaign-end-hint' className='campaigns_field_helper'>Sans date de fin, la campagne dure jusqu'à ce qu'on la termine depuis la liste.</p>
            </>
          )}
        </section>

        <div className='campaigns_form_actions'>
          {submitted && Object.keys(errors).length > 0 && <p className='campaigns_field_error' role='alert'>Corrigez les champs signalés avant {isEdit ? "d'enregistrer" : 'de créer la campagne'}.</p>}
          <button className='campaigns_secondary_button' onClick={close} disabled={submitting}>Annuler</button>
          <button className='campaigns_primary_button' onClick={submit} disabled={submitting} aria-busy={submitting || undefined}>
            {isEdit
              ? (submitting ? 'Enregistrement…' : 'Enregistrer')
              : (submitting ? 'Création…' : 'Créer la campagne')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default CampaignFormModal
